/**
 * ExternalUsersTab
 * Tab content for managing external users (contractors, vendors, etc.)
 * Used within the ManageUsers page
 */
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { Plus, Pencil, Trash2, Search, Building, Mail, Phone, Briefcase, UserPlus, ChevronLeft } from 'lucide-react';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { useListDetailScroll } from '../../hooks/useListDetailScroll';
import { useExternalUsers } from '../../hooks/useExternalUsers';
import { useFeature } from '../../hooks/useFeatures';
import { CreateExternalUserPayload, UpdateExternalUserPayload } from '../../types/guestAccess';
import HeldKeysSection from '../KeyRegister/HeldKeysSection';
import ExternalUserLinkSection from './ExternalUserLinkSection';

interface ExternalUsersTabProps {
    getUserName?: (uid?: string) => string;
    selectedUid?: string;
    /** URL of one user (null = the list); the list items are links to it */
    userPath: (uid: string | null) => string;
    /** Changes the selection after an action (null after a delete) */
    onSelectUser?: (uid: string | null) => void;
}

// Get initials for avatars (matches internal/deleted user layout)
const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const formatDate = (dateString?: string) => {
    if (!dateString) return 'Okänt';
    try {
        return new Date(dateString).toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch {
        return 'Ogiltigt datum';
    }
};

const ExternalUsersTab: React.FC<ExternalUsersTabProps> = ({ getUserName, selectedUid, userPath, onSelectUser }) => {
    const {
        users,
        loading,
        error,
        submitting,
        createUser,
        updateUser,
        deleteUser,
        clearError,
    } = useExternalUsers();
    const keyRegisterEnabled = useFeature('keyRegister');

    const [search, setSearch] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [replacementName, setReplacementName] = useState('Borttagen användare');
    const [modalError, setModalError] = useState<string | null>(null);

    // Selected user is derived from the URL (owned by the parent ManageUsers page)
    const selectedUser = selectedUid ? users.find(u => u.uid === selectedUid) ?? null : null;

    // Phones show either the user list or the open user, never both
    const isSmallScreen = useIsSmallScreen();
    const mobileView = isSmallScreen && selectedUser ? 'detail' : 'list';
    const { containerRef, rememberListScroll } = useListDetailScroll<HTMLDivElement>(mobileView === 'detail');

    // Form state for create/edit
    const [formData, setFormData] = useState<CreateExternalUserPayload>({
        name: '',
        email: '',
        phone: '',
        company: '',
        role: '',
    });

    // Filter users by search and sort alphabetically by name
    const filteredUsers = useMemo(() => {
        const searchLower = search.trim().toLowerCase();
        const matched = searchLower
            ? users.filter(
                  u =>
                      u.name.toLowerCase().includes(searchLower) ||
                      u.email.toLowerCase().includes(searchLower) ||
                      (u.company || '').toLowerCase().includes(searchLower) ||
                      (u.role || '').toLowerCase().includes(searchLower)
              )
            : users;
        return [...matched].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [users, search]);

    // Handlers
    const handleOpenCreate = () => {
        setFormData({ name: '', email: '', phone: '', company: '', role: '' });
        setModalError(null);
        setShowCreateModal(true);
    };

    const handleOpenEdit = () => {
        if (!selectedUser) return;
        setFormData({
            name: selectedUser.name,
            email: selectedUser.email,
            phone: selectedUser.phone || '',
            company: selectedUser.company || '',
            role: selectedUser.role || '',
        });
        setModalError(null);
        setShowEditModal(true);
    };

    const handleOpenDelete = () => {
        if (!selectedUser) return;
        setReplacementName('Borttagen användare');
        setModalError(null);
        setShowDeleteModal(true);
    };

    const handleCreate = async () => {
        if (!formData.name.trim() || !formData.email.trim()) {
            setModalError('Namn och e-post krävs');
            return;
        }

        try {
            await createUser(formData);
            setShowCreateModal(false);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Kunde inte skapa användare';
            setModalError(message);
        }
    };

    const handleEdit = async () => {
        if (!selectedUser || !formData.name.trim() || !formData.email.trim()) {
            setModalError('Namn och e-post krävs');
            return;
        }

        const payload: UpdateExternalUserPayload = {
            uid: selectedUser.uid,
            name: formData.name,
            email: formData.email,
            phone: formData.phone || undefined,
            company: formData.company || undefined,
            role: formData.role || undefined,
        };

        try {
            await updateUser(payload);
            setShowEditModal(false);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Kunde inte uppdatera användare';
            setModalError(message);
        }
    };

    const handleDelete = async () => {
        if (!selectedUser) return;

        if (!replacementName.trim()) {
            setModalError('Ersättningsnamn krävs');
            return;
        }

        try {
            await deleteUser(selectedUser.uid, replacementName.trim());
            setShowDeleteModal(false);
            onSelectUser?.(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Kunde inte ta bort användare';
            setModalError(message);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <>
            {error && (
                <Alert variant="danger" onClose={clearError} dismissible className="mb-3">
                    {error}
                </Alert>
            )}

            {/* Split-pane content (matches internal/deleted user layout) */}
            <div className="manage-users-page" ref={containerRef} data-mobile-view={mobileView}>
                {/* Left Pane - User List */}
                <div className="manage-users-left-pane" onClickCapture={rememberListScroll}>
                    <div className="left-pane-header">
                        <Button variant="primary" size="sm" onClick={handleOpenCreate}>
                            <Plus size={16} className="me-1" />
                            Lägg till
                        </Button>
                    </div>
                    <div className="user-list-container">
                        <div className="position-relative mb-2">
                            <Search size={16} className="position-absolute" style={{ left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }} />
                            <Form.Control
                                type="text"
                                placeholder="Sök på namn, e-post, företag..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ paddingLeft: 34 }}
                                size="sm"
                            />
                        </div>

                        {filteredUsers.length === 0 ? (
                            <div className="empty-user-list">
                                {search ? 'Inga användare matchar din sökning' : 'Inga externa användare'}
                            </div>
                        ) : (
                            filteredUsers.map((user) => (
                                <Link
                                    key={user.uid}
                                    to={userPath(user.uid)}
                                    className={`user-item ${selectedUser?.uid === user.uid ? 'selected' : ''}`}
                                    aria-current={selectedUser?.uid === user.uid ? 'page' : undefined}
                                >
                                    <div className="user-item-avatar">
                                        {getInitials(user.name)}
                                    </div>
                                    <div className="user-item-info">
                                        <div className="user-item-name">{user.name}</div>
                                        <div className="user-item-meta">
                                            {user.company ? `${user.email} · ${user.company}` : user.email}
                                        </div>
                                    </div>
                                    {user.role && (
                                        <div className="user-item-badges">
                                            <Badge bg="secondary" title={user.role}>{user.role}</Badge>
                                        </div>
                                    )}
                                </Link>
                            ))
                        )}
                    </div>
                </div>

                {/* Right Pane - Detail */}
                <div className="manage-users-right-pane">
                    {mobileView === 'detail' && (
                        <Link to={userPath(null)} className="manage-users-back-button">
                            <ChevronLeft size={20} aria-hidden="true" />
                            Alla externa användare
                        </Link>
                    )}
                    {selectedUser ? (
                        <div className="user-detail-pane">
                            <div className="user-profile-header">
                                <div className="user-profile-avatar">
                                    {getInitials(selectedUser.name)}
                                </div>
                                <div className="user-profile-info">
                                    <h3 className="user-profile-name">{selectedUser.name}</h3>
                                    {selectedUser.company && (
                                        <div className="user-profile-uid">{selectedUser.company}</div>
                                    )}
                                </div>
                                <div className="user-profile-actions">
                                    <Button variant="outline-primary" size="sm" onClick={handleOpenEdit} disabled={submitting}>
                                        <Pencil size={14} className="me-1" />
                                        Redigera
                                    </Button>
                                    <Button variant="outline-danger" size="sm" onClick={handleOpenDelete} disabled={submitting}>
                                        <Trash2 size={14} className="me-1" />
                                        Ta bort
                                    </Button>
                                </div>
                            </div>

                            {/* Contact section */}
                            <div className="user-detail-card">
                                <h6 className="user-detail-card-title">Kontakt</h6>
                                <div className="user-activity-grid">
                                    <div className="user-activity-item">
                                        <div className="user-activity-icon">
                                            <Mail size={18} />
                                        </div>
                                        <div className="user-activity-info">
                                            <div className="user-activity-label">E-post</div>
                                            <div className="user-activity-value">{selectedUser.email}</div>
                                        </div>
                                    </div>
                                    {selectedUser.phone && (
                                        <div className="user-activity-item">
                                            <div className="user-activity-icon">
                                                <Phone size={18} />
                                            </div>
                                            <div className="user-activity-info">
                                                <div className="user-activity-label">Telefon</div>
                                                <div className="user-activity-value">{selectedUser.phone}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Information section */}
                            {(selectedUser.company || selectedUser.role) && (
                                <div className="user-detail-card">
                                    <h6 className="user-detail-card-title">Information</h6>
                                    <div className="user-activity-grid">
                                        {selectedUser.company && (
                                            <div className="user-activity-item">
                                                <div className="user-activity-icon">
                                                    <Building size={18} />
                                                </div>
                                                <div className="user-activity-info">
                                                    <div className="user-activity-label">Företag</div>
                                                    <div className="user-activity-value">{selectedUser.company}</div>
                                                </div>
                                            </div>
                                        )}
                                        {selectedUser.role && (
                                            <div className="user-activity-item">
                                                <div className="user-activity-icon">
                                                    <Briefcase size={18} />
                                                </div>
                                                <div className="user-activity-info">
                                                    <div className="user-activity-label">Roll</div>
                                                    <div className="user-activity-value">{selectedUser.role}</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Personal guest link (admin-only callable; remounts per user) */}
                            <ExternalUserLinkSection key={selectedUser.uid} uid={selectedUser.uid} getUserName={getUserName} />

                            {/* Keys held (settings is admin-only, so the admin-only keyRegister root is readable) */}
                            {keyRegisterEnabled && <HeldKeysSection holder={{ kind: 'external', key: selectedUser.uid }} variant="card" />}

                            {/* Activity section */}
                            <div className="user-detail-card">
                                <h6 className="user-detail-card-title">Aktivitet</h6>
                                <div className="user-activity-grid">
                                    <div className="user-activity-item">
                                        <div className="user-activity-icon">
                                            <UserPlus size={18} />
                                        </div>
                                        <div className="user-activity-info">
                                            <div className="user-activity-label">Skapad</div>
                                            <div className="user-activity-value">
                                                {formatDate(selectedUser.createdAt)}
                                                {getUserName && selectedUser.createdBy && (
                                                    <> av {getUserName(selectedUser.createdBy)}</>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="text-muted">
                                Välj en användare från listan för att visa detaljer
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Lägg till extern användare</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalError && <Alert variant="danger">{modalError}</Alert>}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Namn *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Anna Andersson"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>E-post *</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="anna@foretag.se"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Telefon</Form.Label>
                            <Form.Control
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="070-123 45 67"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Företag</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.company}
                                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                placeholder="T.ex. ABC Bygg AB"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Roll</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                placeholder="T.ex. rörmokare"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Avbryt
                    </Button>
                    <Button variant="primary" onClick={handleCreate} disabled={submitting}>
                        {submitting ? 'Skapar...' : 'Skapa'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Redigera extern användare</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalError && <Alert variant="danger">{modalError}</Alert>}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Namn *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>E-post *</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Telefon</Form.Label>
                            <Form.Control
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Företag</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.company}
                                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Roll</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Avbryt
                    </Button>
                    <Button variant="primary" onClick={handleEdit} disabled={submitting}>
                        {submitting ? 'Sparar...' : 'Spara'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Ta bort extern användare</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalError && <Alert variant="danger">{modalError}</Alert>}
                    <Alert variant="warning">
                        <Alert.Heading>Är du säker på att du vill ta bort denna användare?</Alert.Heading>
                        <p className="mb-2"><strong>{selectedUser?.name}</strong> ({selectedUser?.uid})</p>
                        <hr />
                        <p className="mb-1">Följande kommer att hända:</p>
                        <ul className="mb-0">
                            <li>Den personliga länken återkallas och alla delegeringar tas bort</li>
                            <li>Kontaktuppgifter (e-post, telefon, företag, roll) tas bort</li>
                            <li>Ersättningsnamnet visas där användarens namn tidigare visades</li>
                            <li>Historiska referenser förblir intakta</li>
                        </ul>
                    </Alert>
                    <Form.Group className="mb-3">
                        <Form.Label>Ersättningsnamn</Form.Label>
                        <Form.Control
                            type="text"
                            value={replacementName}
                            onChange={(e) => setReplacementName(e.target.value)}
                            placeholder="Ange ersättningsnamn"
                            required
                        />
                        <Form.Text className="text-muted">
                            Detta namn visas i applikationen istället för användarens riktiga namn.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={submitting}>
                        {submitting ? 'Tar bort...' : 'Ta bort'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ExternalUsersTab;
